// DragNote (Components, BL-189): the note by the pointer while something is dragged: where it would go
// (its head), what's known about going there (each line ✓ yes, ! no, ? not known), and what letting go
// does (its foot). Red when it can't go there. It is drawn for the eye only (aria-hidden): the screen
// that drags it says the same in a live region. Fixed over the screen; the screen places it, by `at`
// (beside the pointer) or by moving it through `ref` as the pointer moves. The board's visit drag and
// its grouping's division and technician drags are its uses. Reads only --dragnote-*.
import type { Ref, ReactNode } from 'react';
import './DragNote.css';

export interface DragNoteLine {
  text: ReactNode;
  /** true: it's fine; false: it stands in the way; left out: not known. */
  ok?: boolean;
}

export interface DragNoteProps {
  /** Where it would go: "2–3:30 p.m., Sam Rivera". */
  head: ReactNode;
  lines?: readonly DragNoteLine[];
  /** What letting go does: "Press Enter to check the move first". */
  foot?: ReactNode;
  /** refuse: it can't go there, drawn red. */
  tone?: 'refuse';
  /** Its top left corner on the screen, in px; leave it out to place it through `ref`. */
  at?: { x: number; y: number };
  ref?: Ref<HTMLDivElement>;
}

const MARK = { ok: 'ok', bad: 'bad', unk: 'unk' } as const;

export function DragNote({ head, lines = [], foot, tone, at, ref }: DragNoteProps) {
  return (
    <div
      ref={ref}
      className="fl-dragnote"
      data-tone={tone}
      aria-hidden="true"
      style={at ? { left: at.x, top: at.y } : undefined}
    >
      <b className="fl-dragnote__head">{head}</b>
      {lines.map((l, i) => (
        <span
          key={i}
          className="fl-dragnote__line"
          data-mark={l.ok === false ? MARK.bad : l.ok ? MARK.ok : MARK.unk}
        >
          {l.text}
        </span>
      ))}
      {foot != null ? <i className="fl-dragnote__foot">{foot}</i> : null}
    </div>
  );
}
